/**
 * Printable roadmap snapshot — renders outside Planning chrome for clean @page sizing.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { PrintChrome } from '@/components/print/PrintChrome';
import { useRoadmapStore } from '@/stores/prioritization/roadmapStore';
import type { RoadmapCellRelationship, RoadmapNode } from '@/stores/prioritization/roadmapStore';
import type { PrintPaperId } from '@/lib/print/paperPresets';
import {
  PRINT_PAPER_PRESETS,
  getPrintPaperPreset,
  loadSavedPrintPaperId,
  savePrintPaperId,
} from '@/lib/print/paperPresets';
import { isPlanNodeAwaitingFirstSave } from '@/lib/planNodeDraft';
import { isRoadmapPlanningNavState } from './roadmapPlanningNavState';

function graphVisible(nodes: RoadmapNode[]): RoadmapNode[] {
  return nodes.filter((n) => !isPlanNodeAwaitingFirstSave(n.text));
}

/** Inline hint for print only — divider rows are omitted entirely (cosmetic in the editor). */
function connectorParenthetical(rel: RoadmapCellRelationship): string | null {
  switch (rel) {
    case 'sequential':
      return '(depends on ↑)';
    case 'parallel':
      return '(both required)';
    case 'then':
      return '(then)';
    case 'divider':
      return null;
    default:
      return null;
  }
}

function CellStackPrint({ nodes }: { nodes: RoadmapNode[] }) {
  const stack = nodes.filter((n) => n.cellRelationship !== 'divider');
  return (
    <ol className="list-decimal pl-4 space-y-2">
      {stack.map((n, idx) => {
        const paren = idx > 0 ? connectorParenthetical(n.cellRelationship) : null;
        const body = n.text?.trim() || 'Untitled';
        return (
          <li key={n.id} className="print-field-block">
            <div className="whitespace-pre-wrap text-[var(--text-pri)] print:text-black">
              {body}
              {paren ? (
                <span className="text-[var(--text-sec)] print:text-[var(--text-sec)]"> {paren}</span>
              ) : null}
            </div>
            {n.isGoal ? (
              <div className="mt-1 text-[9px] text-[var(--text-ter)] print:text-[var(--text-ter)]">Goal</div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function RoadmapPrintPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const documents = useRoadmapStore((s) => s.documents);
  const nodes = useRoadmapStore((s) => s.nodes);
  const swimlanes = useRoadmapStore((s) => s.swimlanes);
  const milestones = useRoadmapStore((s) => s.milestones);
  const isLoading = useRoadmapStore((s) => s.isLoading);
  const fetchDocuments = useRoadmapStore((s) => s.fetchDocuments);
  const fetchRoadmap = useRoadmapStore((s) => s.fetchRoadmap);

  const [paperId, setPaperId] = useState<PrintPaperId>(() => loadSavedPrintPaperId());

  useEffect(() => {
    savePrintPaperId(paperId);
  }, [paperId]);

  useEffect(() => {
    if (!id) return;
    if (documents.length === 0) fetchDocuments();
    fetchRoadmap(id);
    // Match RoadmapEditor: load keyed by route id without re-running on cache churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doc = id ? documents.find((d) => d.id === id) : undefined;
  const roadmapNodes = id ? graphVisible(nodes.filter((n) => n.roadmapId === id)) : [];

  const laneList = id
    ? swimlanes.filter((s) => s.roadmapId === id).sort((a, b) => a.position - b.position)
    : [];

  const mileList = id
    ? milestones.filter((m) => m.roadmapId === id).sort((a, b) => a.position - b.position)
    : [];

  const preset = useMemo(() => getPrintPaperPreset(paperId), [paperId]);

  const milestoneOrder = useMemo(() => {
    const map = new Map<string, number>();
    mileList.forEach((m) => map.set(m.id, m.position));
    return (mid: string | null): number =>
      mid != null && map.has(mid) ? (map.get(mid) as number) : 9999;
  }, [mileList]);

  const nodesInCell = (
    laneId: string | null,
    milestoneId: string | null,
  ): RoadmapNode[] =>
    roadmapNodes
      .filter((n) => n.laneId === laneId && n.milestoneId === milestoneId)
      .sort((a, b) => a.position - b.position);

  if (!id) {
    return <div className="p-8 text-center text-sm text-[var(--text-sec)]">Missing roadmap.</div>;
  }

  const loadingFresh = isLoading && !doc;
  const docTitle =
    doc?.title?.trim() ? `${doc.title.trim()} — Roadmap print` : 'Roadmap — Print';

  const navState = location.state;
  const navFromPrioritization = isRoadmapPlanningNavState(navState);
  const backHref = navFromPrioritization ? navState.roadmapReturnToPrioritization : `/prioritization/roadmap/${id}`;
  const backLabel = navFromPrioritization ? 'Back to Strategy' : 'Back to editor';
  const backState = navFromPrioritization ? undefined : navState;

  return (
    <PrintChrome
      documentTitle={docTitle}
      pageSizeCss={preset.pageSizeCss}
      previewMaxWidthMm={preset.previewMaxWidthMm}
      backHref={backHref}
      backLabel={backLabel}
      backState={backState}
      pagedRenderKey={`${id}|${paperId}|${doc?.updatedAt ?? '0'}|${roadmapNodes.length}`}
      toolbarAside={
        <label className="flex items-center gap-2 text-xs text-[var(--text-sec)] print:hidden">
          <span>Paper size</span>
          <select
            value={paperId}
            onChange={(e) => setPaperId(e.target.value as PrintPaperId)}
            className="border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-xs text-[var(--text-pri)] shadow-sm"
          >
            {PRINT_PAPER_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      }
    >
      <div className="w-full max-w-none space-y-6 text-[12px] text-[var(--text-pri)] print:text-black">
        {loadingFresh ? (
          <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading roadmap…
          </div>
        ) : !doc ? (
          <p className="text-sm text-[var(--text-sec)]">Roadmap not found.</p>
        ) : (
          <>
            <header className="print-field-block w-full max-w-none border-b border-[var(--rule)] pb-4 space-y-1 print:border-b-0 print:pb-5">
              <h1 className="text-2xl font-semibold tracking-tight">{doc.title || 'Roadmap'}</h1>
              {doc.objective?.trim() ? (
                <p className="text-[var(--text-sec)] leading-snug">{doc.objective}</p>
              ) : null}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[var(--text-ter)]">
                {doc.futurePoint?.trim() ? <span>Horizon: {doc.futurePoint}</span> : null}
                {doc.definedSuccess?.trim() ? (
                  <span className="max-w-[60ch]">Success criteria: {doc.definedSuccess}</span>
                ) : null}
              </div>
            </header>

            {mileList.length > 0 && laneList.length > 0 ? (
              <section className="print-field-block w-full max-w-none overflow-x-auto print:overflow-visible space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  Grid · swimlanes × milestones
                </h2>
                <table className="w-full max-w-none table-fixed border-collapse border border-[var(--rule)] text-[11px]">
                  <thead>
                    <tr>
                      <th className="border border-[var(--rule)] bg-[var(--hover-tint)] px-2 py-1 text-left">Swimlane</th>
                      {mileList.map((m) => (
                        <th key={m.id} className="border border-[var(--rule)] bg-[var(--hover-tint)] px-2 py-1">
                          <div>{m.name}</div>
                          {m.condition?.trim() ? (
                            <div className="font-normal text-[var(--text-sec)]">{m.condition}</div>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {laneList.map((lane) => (
                      <tr key={lane.id}>
                        <td className="border border-[var(--rule)] align-top px-2 py-1 font-semibold">
                          {lane.name}
                        </td>
                        {mileList.map((m) => {
                          const cell = nodesInCell(lane.id, m.id);
                          return (
                            <td key={m.id} className="border border-[var(--rule)] align-top px-2 py-1">
                              {cell.length === 0 ? (
                                <span className="text-[var(--text-ter)]">—</span>
                              ) : (
                                <CellStackPrint nodes={cell} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : mileList.length > 0 ? (
              <section className="print-field-block w-full max-w-none overflow-x-auto print:overflow-visible space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  By milestone column
                </h2>
                <table className="w-full max-w-none table-fixed border-collapse border border-[var(--rule)] text-[11px]">
                  <thead>
                    <tr>
                      {mileList.map((m) => (
                        <th key={m.id} className="border border-[var(--rule)] bg-[var(--hover-tint)] px-2 py-1">
                          <div>{m.name}</div>
                          {m.condition?.trim() ? (
                            <div className="font-normal text-[var(--text-sec)]">{m.condition}</div>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {mileList.map((m) => (
                        <td key={m.id} className="border border-[var(--rule)] align-top px-2 py-1">
                          <CellStackPrint nodes={nodesInCell(null, m.id)} />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </section>
            ) : laneList.length > 0 ? (
              <section className="print-field-block w-full max-w-none overflow-x-auto print:overflow-visible space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  By swimlane
                </h2>
                <table className="w-full max-w-none table-fixed border-collapse border border-[var(--rule)] text-[11px]">
                  <thead>
                    <tr>
                      <th className="border border-[var(--rule)] bg-[var(--hover-tint)] px-2 py-1 text-left">
                        Swimlane
                      </th>
                      <th className="border border-[var(--rule)] bg-[var(--hover-tint)] px-2 py-1 text-left">
                        Conditions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {laneList.map((lane) => {
                      const ordered = roadmapNodes
                        .filter((n) => n.laneId === lane.id)
                        .slice()
                        .sort((a, b) => {
                          const d = milestoneOrder(a.milestoneId) - milestoneOrder(b.milestoneId);
                          return d !== 0 ? d : a.position - b.position;
                        });

                      return (
                        <tr key={lane.id}>
                          <td className="border border-[var(--rule)] align-top px-2 py-1 font-semibold">
                            {lane.name}
                          </td>
                          <td className="border border-[var(--rule)] align-top px-2 py-1">
                            {ordered.length === 0 ? (
                              <span className="text-[var(--text-ter)]">—</span>
                            ) : (
                              <CellStackPrint nodes={ordered} />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            ) : (
              <section className="print-field-block w-full max-w-none space-y-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  All visible conditions
                </h2>
                <CellStackPrint
                  nodes={roadmapNodes
                    .slice()
                    .sort((a, b) => {
                      const la = laneList.findIndex((l) => l.id === (a.laneId ?? ''));
                      const lb = laneList.findIndex((l) => l.id === (b.laneId ?? ''));
                      if (la !== lb && la >= 0 && lb >= 0) return la - lb;
                      return a.position - b.position;
                    })}
                />
              </section>
            )}
          </>
        )}
      </div>
    </PrintChrome>
  );
}
